import { historyEn } from '@/features/vehicle-history/i18n'
import { statisticsGuideEn } from '@/features/network-statistics/guideI18n'
import { routeStatisticsEn } from '@/features/route-statistics/i18n'
import { journeyPlannerEn } from '@/features/journey-planner/i18n'

export const en = {
  app: {
    slogan: 'Transit for people. Data for nerds.',
  },
  common: {
    close: 'Close',
    retry: 'Try again',
    refresh: 'Refresh',
    loading: 'Loading…',
    minutesShort: '{count} min',
    now: 'now',
    yes: 'Yes',
  },
  toolbar: {
    themeLight: 'Use light theme',
    themeDark: 'Use dark theme',
    language: 'Switch to Czech',
    statistics: 'Statistics',
    statisticsMenu: 'Statistics menu',
    networkStatistics: 'Network',
    networkStatisticsDescription: 'Traffic and delay on individual segments',
    routeStatistics: 'Routes',
    routeStatisticsDescription: 'Reliability, service and route length',
  },
  activeVehicles: {
    navigation: 'Main navigation',
    navigationLabel: 'Active vehicles',
    map: 'Map',
    backToMap: 'Back to map',
    liveOverview: 'Live operations',
    title: 'Active vehicles by route',
    description: 'Every vehicle currently in service in one place – model, direction, last known position, delay and data freshness.',
    vehicleCount: 'Active vehicles: {count}',
    routeVehicleCount: 'Vehicles on route: {count}',
    updated: 'Data {age}',
    loading: 'Loading active vehicles…',
    loadError: 'Active vehicles could not be loaded right now.',
    refreshError: 'Refresh failed. Showing the last known data.',
    empty: 'There are no active vehicles available right now.',
    emptyHint: 'A vehicle will appear here as soon as fresh in-service data is available.',
    vehicle: 'Vehicle',
    vehicleModel: 'Vehicle model',
    direction: 'Direction',
    lastPosition: 'Last position',
    delay: 'Delay',
    freshness: 'Updated',
    directionBetween: '{origin} → {destination}',
    directionTo: 'towards {destination}',
    directionUnknown: 'Direction unknown',
    coordinates: '{latitude}, {longitude}',
    delayUnknown: 'no data',
    openVehicle: 'Open vehicle {number} on route {route} on the map',
    openStaleVehicle: 'Open vehicle {number} on route {route} on the map; position is stale',
    stalePosition: 'Stale position',
    filterLabel: 'Filter by transport mode',
    filterMode: '{mode}: {count} active vehicles',
    filteredEmpty: 'No vehicles match the selected filter.',
    filteredEmptyHint: 'Choose another transport mode or show all vehicles again.',
    showAll: 'Show all',
  },
  journeys: journeyPlannerEn,
  statistics: {
    navigationLabel: 'Network statistics',
    backToMap: 'Back to map',
    eyebrow: 'Network analysis',
    title: 'Segment statistics',
    description: 'Traffic, delays, period-over-period changes, directional imbalance and data confidence on one diagram.',
    metric: 'Metric',
    metric_passages: 'Passages',
    metric_routes: 'Route count',
    metric_passagesPerRoute: 'Passages per route',
    metric_hourlyPeak: 'Hourly peak',
    metric_trafficChange: 'Traffic change',
    metric_directionalImbalance: 'Directional imbalance',
    metric_delayIncrease: 'Delay increase',
    metric_delayRecovery: 'Delay recovery',
    metric_delayImpact: 'Net delay impact',
    metric_delayPersistence: 'Persistent delay',
    metric_delayChange: 'Delay change',
    metric_criticality: 'Segment criticality',
    metric_delayVariability: 'Delay variability',
    metric_travelTime: 'Travel time',
    metric_headwayIrregularity: 'Headway irregularity',
    metric_bunching: 'Service bunching',
    metric_dataConfidence: 'Data confidence',
    help_passages: 'Total recorded passages in the selected direction.',
    help_routes: 'Distinct routes that used the segment in the selected direction.',
    help_passagesPerRoute: 'Average number of passages per route.',
    help_hourlyPeak: 'The busiest local clock hour by average passages per day.',
    help_trafficChange: 'Passage change against the immediately preceding period of equal length.',
    help_directionalImbalance: 'Traffic difference between both directions of the same segment.',
    help_delayIncrease: 'Average delay gained between adjacent stops.',
    help_delayRecovery: 'Segments where services reduce their delay on average.',
    help_delayImpact: 'Net sum of delay changes across all usable samples.',
    help_delayPersistence: 'Share of days on which delay increased on average.',
    help_delayChange: 'Change in average delay gain against the preceding period of equal length.',
    help_criticality: 'A combination of traffic intensity and average delay gain.',
    help_delayVariability: 'Standard deviation of the average daily delay change.',
    help_travelTime: 'Average observed time from departure at the first stop to arrival at the second.',
    help_headwayIrregularity: 'Coefficient of variation of actual headways for the same route and hour; higher means less regular service.',
    help_bunching: 'Share of services whose actual headway is at most half of the scheduled headway.',
    help_dataConfidence: 'A score based on covered days and sufficient delay samples.',
    period: 'Period',
    period_today: 'Today',
    period_7d: '7 days',
    period_28d: '28 days',
    period_90d: '90 days',
    ranking: 'Segment ranking',
    segmentCount: '{count} segments',
    loading: 'Calculating segment statistics…',
    loadError: 'Statistics could not be loaded right now.',
    empty: 'No data is available for the selected period.',
    emptyRange: 'No aggregated data is available for {from}–{to}.',
    emptyPartial: 'The source marked this period as incomplete.',
    show28d: 'Show 28 days',
    lines: 'Routes:',
    routeValue: '{count} routes',
    passageValue: '{count} passages',
    passagesPerRouteValue: '{count} / route',
    hourlyPeakValue: '{count}/h · {hour}',
    segmentDetail: 'Segment detail',
    passages: 'Passages',
    routes: 'Routes',
    delayIncrease: 'Avg. change',
    samples: 'Delay samples',
    totalDelayImpact: 'Net impact',
    problemDays: 'Days with increase',
    previousPeriod: 'Vs. prior period',
    reversePassages: 'Reverse direction',
    observedDays: 'Covered days',
    variability: 'Variability',
    peakHour: 'Peak hour',
    averageHourlyPassages: 'Avg. passages/hour',
    travelTime: 'Travel time',
    travelTimeSamples: 'Travel-time samples',
    headwayIrregularity: 'Irregularity',
    headwaySamples: 'Headway samples',
    bunching: 'Bunched services',
    routeBreakdown: 'Breakdown by route',
    route: 'Route',
    partialData: 'The period contains an incomplete or still-open service day.',
    legend: 'Selected metric intensity',
    legendLow: 'less',
    legendHigh: 'more',
    legendDecrease: 'decrease',
    legendIncrease: 'increase',
    legendImprovement: 'improvement',
    legendWorsening: 'worsening',
    segmentAria: 'Segment from {from} to {to}',
    guideLink: 'How statistics work',
    guideLinkAria: 'Learn how statistics work (opens in a new tab)',
  },
  statisticsGuide: statisticsGuideEn,
  routeStatistics: routeStatisticsEn,
  search: {
    label: 'Search for a service or stop',
    placeholder: 'Route, stop or vehicle…',
    clear: 'Clear search',
    routes: 'Routes',
    stops: 'Stops',
    vehicles: 'Vehicles',
    routeDescription: 'Route {route}',
    vehicleDescription: 'Fleet number {number}',
    noResults: 'Nothing found in the current map area.',
    hint: 'Search by route number, stop or fleet number.',
  },
  filters: {
    label: 'Transport modes',
    show: 'Show filters',
    all: 'All',
    clear: 'Clear filters',
  },
  modes: {
    tram: 'Trams',
    bus: 'Buses',
    trolleybus: 'Trolleybuses',
    metro: 'Metro',
    train: 'Trains',
    ferry: 'Ferries',
    funicular: 'Funiculars',
    unknown: 'Other',
  },
  modeSingular: {
    tram: 'Tram',
    bus: 'Bus',
    trolleybus: 'Trolleybus',
    metro: 'Metro',
    train: 'Train',
    ferry: 'Ferry',
    funicular: 'Funicular',
    unknown: 'Vehicle',
  },
  status: {
    live: 'Live data',
    refreshing: 'Refreshing',
    updatedNow: 'Updated just now',
    updatedAgo: 'Updated {time} ago',
    ageSeconds: '{count} sec',
    ageMinutes: '{count} min',
    ageHours: '{count} hr',
    stale: 'Data is stale',
    providerError: 'Live data is temporarily unavailable. Showing the last known positions.',
    offline: 'You are offline. Last known data remains on the map.',
    noSnapshot: 'Waiting for the first transit snapshot.',
  },
  map: {
    label: 'Live public transport map',
    viewSwitchLabel: 'Map view',
    classicView: 'Classic map',
    schematicView: 'Diagram',
    servicePeriodLabel: 'Route service period',
    dayService: 'Day',
    nightService: 'Night',
    schematicModeLabel: 'Diagram transport mode',
    allTransportModes: 'All',
    loading: 'Preparing the map and live data…',
    empty: 'No vehicles match these filters in the current area.',
    tilesError: 'The map background could not be loaded.',
    tilesFallback: 'You can still browse transit data without the map background.',
    recenter: 'Focus the selected place',
    vehicleLabel: 'Route {route}, towards {headsign}',
    stopLabel: 'Stop {name}',
    overlapTitle: '{count} objects at this location',
    overlapHint: 'Hover to highlight. Alt+click or Tab cycles through the objects.',
    overlapVehicle: 'Route {route}',
    overlapVehicleMeta: 'Vehicle {number} · towards {headsign}',
    overlapStopMeta: 'Stop',
    schematicLabel: 'Public transport network diagram',
    schematicLoading: 'Preparing the transport network diagram…',
    schematicError: 'The network diagram could not be loaded right now.',
    schematicEmpty: 'No diagram routes match the selected service period and transport modes.',
    schematicFallback: 'Show the classic map',
    schematicZoomIn: 'Zoom in on the diagram',
    schematicZoomOut: 'Zoom out of the diagram',
    schematicBasemap: 'Map background under the diagram',
    schematicRouteBadge: 'Route {route} from terminus {stop}',
  },
  nearby: {
    showLocation: 'Show my location',
    showStops: 'Show nearby stops',
    eyebrow: 'Based on your location',
    title: 'Nearby stops',
    description: 'The closest stops within 1.5 km. Expand a stop for departures grouped by platform.',
    locating: 'Finding your location…',
    locatingHint: 'Your browser may ask for permission to access your location.',
    loading: 'Finding the nearest stops…',
    unsupported: 'This browser cannot determine your location.',
    permissionDenied: 'Location access is not allowed.',
    timeout: 'Your location could not be determined in time.',
    unavailable: 'Your location is currently unavailable.',
    locationErrorHint: 'Check the location permission in your browser settings and try again.',
    loadError: 'Nearby stops could not be loaded.',
    empty: 'No stops were found nearby.',
    emptyHint: 'Try refreshing your location or move to another place.',
    platformCount: 'Platforms and stands: {count}',
    loadingDepartures: 'Loading departures by platform…',
    departuresError: 'Departures for this stop could not be loaded.',
    platform: 'Platform {platform}',
    onlyPlatform: 'Departures from this stop',
    unnamedPlatform: 'Stand {number}',
    openPlatform: 'Open details: {platform}',
    openFullDetail: 'Open all upcoming services',
    platformDeparturesError: 'Departures for this platform are unavailable.',
    distanceMeters: '{distance} m',
    distanceKilometers: '{distance} km',
  },
  detail: {
    backToStopView: 'Back to the stop view',
    expand: 'Expand detail',
    collapse: 'Collapse detail',
    focusVehicle: "Focus the vehicle's current position",
    loadingVehicle: 'Loading vehicle detail and its trip…',
    loadingStop: 'Loading stop and departures…',
    loadingTrip: 'Loading service detail and its route…',
    vehicleNotFound: 'This vehicle is no longer in the current snapshot.',
    stopNotFound: 'The stop could not be found.',
    tripNotFound: 'The service could not be found.',
    loadError: 'The detail could not be loaded right now.',
    vehicle: 'Vehicle',
    stop: 'Stop',
    trip: 'Service',
    departure: 'Departure',
    stopPlatform: 'Platform',
    accessibility: 'Accessibility',
    direction: 'towards {headsign}',
    registration: 'Vehicle {number}',
    model: 'Vehicle model',
    operator: 'Operator',
    delay: 'Delay',
    early: '{minutes} min early',
    onTime: 'On time',
    delayed: '+{minutes} min',
    lastStop: 'Previous stop',
    nextStop: 'Next stop',
    positionAge: 'Position',
    speed: 'Speed',
    speedValue: '{speed} km/h',
    route: 'Trip progress',
    routeUnavailable: 'The exact trip shape is not available.',
    passedStop: 'passed stop',
    nextRouteStop: 'next stop',
    remainingStop: 'remaining stop',
    equipment: 'Equipment',
    wheelchair: 'Wheelchair accessible',
    airConditioned: 'Air conditioning',
    usbChargers: 'USB charging',
    staleVehicle: 'This vehicle position is stale.',
    cancelled: 'Service cancelled',
    platform: 'platform {platform}',
    departures: 'Next departures',
    noDepartures: 'There are no departures in the near future.',
    timetableOnly: 'Only timetable data is available for these departures.',
    realtime: 'live prediction',
    scheduled: 'timetable',
    departureCancelled: 'cancelled',
    accessible: 'accessible service',
    exactTime: '{time} · {source}',
    predictionOnTime: 'Prediction {time}, service is on time',
    predictionOffSchedule: 'Prediction {time}, service is off schedule',
    openTrip: 'Open route {route} service details towards {headsign}',
    nextStops: 'Then via',
    followingStopsAria: 'Following stops towards {headsign}',
  },
  history: historyEn,
}
